<script setup lang="ts">
import {
  CategoryType,
  ImageOrigin,
  type MediaContent,
  type ProductDetails,
} from "mws-ts-v2-sdk";

import { MediaContentType } from "~/types/Catalog";

const { media: mediaItems, product } = defineProps<{
  media: MediaContent[];
  product: ProductDetails;
  hideFollowButton?: boolean;
}>();

const i18n = useI18n();
const { productCarouselOpened, productCarouselChanged } = useAnalytics();

const activeMediaItemIndex = ref(0);
const isActiveMediaMockup = computed(
  () =>
    mediaItems[activeMediaItemIndex.value]?.metadata?.origin ===
    ImageOrigin.Mockup,
);

const mainMediaItemsRef = useTemplateRefsList<HTMLLIElement>();
const mainMediaItemsContainerRef = useTemplateRef("mainMediaItemsContainerRef");

const scrollToMediaItem = async (itemIndex: number) => {
  await nextTick();

  const mediaItemRef = mainMediaItemsRef.value?.[itemIndex];
  const container = mainMediaItemsContainerRef.value;

  // Add defensive checks to prevent DOM errors
  if (mediaItemRef && container && container.contains(mediaItemRef)) {
    const activeMediaItemLeft = mediaItemRef.offsetLeft;
    container.scrollTo({ left: activeMediaItemLeft });
  }
};

const { width } = useWindowSize();
watchDebounced(width, () => scrollToMediaItem(activeMediaItemIndex.value), {
  debounce: 100,
});

watch(activeMediaItemIndex, (newActiveMediaItemIndex) => {
  scrollToMediaItem(newActiveMediaItemIndex);

  const newMediaItem = mediaItems[activeMediaItemIndex.value];
  productCarouselChanged({
    current_image: newMediaItem?.url ?? brokenMediaFallback,
  });
});

const onScrollEndContainer = useDebounceFn(() => {
  const containerScrollLeft = mainMediaItemsContainerRef.value?.scrollLeft ?? 0;

  // Check which media item is visible after swiping and snapping. Set that one as the main media item
  // The thumbnails will also react to this change
  // +- 50 px error margin
  const visibleMediaItemIndex = mainMediaItemsRef.value.findIndex((item) => {
    return (
      containerScrollLeft - 50 < item.offsetLeft &&
      containerScrollLeft + 50 > item.offsetLeft
    );
  });

  if (visibleMediaItemIndex > -1) {
    activeMediaItemIndex.value = visibleMediaItemIndex;
  }
}, 200);

useEventListener(mainMediaItemsContainerRef, "scroll", onScrollEndContainer);

const labelTexts = computed(() => {
  return product.labels.map((label) =>
    translateWithFallback(
      i18n,
      `product.labels.${label.text}`,
      label.text ?? "",
    ),
  );
});

const priceInEuros = computed(() => {
  return product.prices.find(({ currency }) => currency === "EUR")?.price ?? 0;
});

onKeyStroke(["ArrowLeft"], () => {
  if (activeMediaItemIndex.value === 0) return;

  return (activeMediaItemIndex.value -= 1);
});

onKeyStroke(["ArrowRight"], () => {
  if (activeMediaItemIndex.value === mediaItems.length - 1) return;

  return (activeMediaItemIndex.value += 1);
});

const { isTouchDevice } = useDeviceInformation();

const isFullScreenActive = ref(false);
const onOpenFullScreen = (mediaItem: MediaContent) => {
  if (isTouchDevice.value) return;

  isFullScreenActive.value = true;
  productCarouselOpened({
    active_media_item: mediaItem.url ?? brokenMediaFallback,
  });
};

const isMockupInfoDialogOpen = ref(false);

const { t } = useI18n();

const imageAltTag = computed(() => {
  const isFootballShirt = product.type === "football_shirt";

  const teamName = product.categories.find(
    (category) => category.externalReference?.type === CategoryType.Team,
  )?.name;

  const athleteName = product.categories.find(
    (category) => category.externalReference?.type === CategoryType.Athlete,
  )?.name;

  if (!isFootballShirt || !teamName || !athleteName) {
    return product.name;
  }

  return t("product.meta.footballShirts.imageAltTag", {
    PLACEHOLDER_teamName: teamName,
    PLACEHOLDER_athleteName: athleteName,
  });
});
</script>

<template>
  <div
    class="w-full pb-2 md:pb-0 md:max-w-112 lg:max-w-full relative flex flex-col-reverse"
    data-cy="main-container-media"
  >
    <ProductMediaThumbnails
      class="px-4 pt-2 md:px-0 md:pb-0 lg:p-0 lg:w-24"
      :media="mediaItems"
      :image-alt-tag="imageAltTag"
      :active-media-item-index="activeMediaItemIndex"
      @active-image-change="(index) => (activeMediaItemIndex = index)"
    />

    <!-- Active image -->
    <div class="w-full lg:pl-24 lg:ml-3">
      <div class="relative max-w-196 mx-auto">
        <ul
          ref="mainMediaItemsContainerRef"
          class="flex w-full overflow-x-auto md:overflow-x-hidden relative snap-x snap-mandatory md:snap-none"
        >
          <li
            v-for="(mediaItem, index) in mediaItems"
            :ref="mainMediaItemsRef.set"
            :key="mediaItem.url ?? index"
            class="min-w-full w-full flex justify-center snap-start cursor-pointer aspect-[4/5] bg-platform-bg-2 md:bg-platform-bg-2"
            :data-test="`product-media-gallery-main-image-${index}${activeMediaItemIndex === index ? '-active' : ''}`"
          >
            <NuxtImg
              v-if="mediaItem.type === MediaContentType.Image"
              :src="mediaItem.url ?? brokenMediaFallback"
              provider="imgix"
              :alt="imageAltTag"
              preset="productHighlight"
              class="object-contain w-full transition duration-300"
              :class="{
                'drop-shadow-product-media': activeMediaItemIndex === index,
              }"
              :data-cy="
                activeMediaItemIndex === index
                  ? 'product-main-media'
                  : undefined
              "
              @click="onOpenFullScreen"
              @keydown="onOpenFullScreen"
            />
            <BaseVideoPlayer
              v-if="mediaItem.type === MediaContentType.Video"
              :src="mediaItem.url ?? brokenMediaFallback"
              :controls="false"
              :data-cy="
                activeMediaItemIndex === index
                  ? 'product-main-media'
                  : undefined
              "
              @click="onOpenFullScreen"
              @keydown="onOpenFullScreen"
            />
          </li>
        </ul>

        <div
          class="absolute top-4 left-4 lg:top-6 lg:left-6 flex flex-row gap-1 flex-wrap max-w-[calc(100%-100px)] md:max-w-[calc(100%-200px)]"
        >
          <BaseTag
            v-for="(label, index) in product.labels"
            :key="label.id"
            width="fit-content"
            class="rounded-sm"
            size="xs"
          >
            {{ labelTexts[index] }}
          </BaseTag>
        </div>

        <ProductFollowButton
          v-if="!hideFollowButton"
          :id="product.id"
          class="absolute top-4 right-4 lg:top-6 lg:right-6 p-1.5 bg-platform-bg-1"
          :name="product.name"
          :slug="product.slug"
          size="large"
          :is-followed-by-user="product.isFollowedByUser ?? false"
          :price-in-euros="priceInEuros"
        />

        <div
          v-if="isActiveMediaMockup"
          class="absolute bottom-4 left-4 lg:bottom-6 lg:left-6 text-platform-text-3 flex flex-wrap max-w-[calc(100%-150px)] lg:max-w-[calc(100%-220px)]"
          data-test="product-media-gallery-mockup-description"
        >
          <p class="text-xs leading-tight">
            {{ $t("product.mockup") }}&nbsp;
            <button
              data-cy="mockup-info-button"
              @click="() => (isMockupInfoDialogOpen = true)"
            >
              <BaseIcon
                name="circleInfo"
                :size="14"
                class="relative top-1"
              />
            </button>
          </p>
          <BaseDialog
            v-model="isMockupInfoDialogOpen"
            size="md"
          >
            <BaseDialogContent>
              <template #title>{{ $t("product.mockupInfoTitle") }}</template>
              <template #content>
                <p>
                  {{ $t("product.labels.descriptions.mockup") }}
                </p>
              </template>
            </BaseDialogContent>
          </BaseDialog>
        </div>

        <BaseNavigationButtons
          v-if="mediaItems.length > 1"
          :active-item-index="activeMediaItemIndex"
          :items-length="mediaItems.length"
          class="absolute right-4 bottom-4 lg:right-6 lg:bottom-6"
          @index-change="(index) => (activeMediaItemIndex = index)"
        />
      </div>
    </div>
  </div>
  <!-- Client only because our zoom package does not work on the server -->
  <ClientOnly>
    <LazyProductMediaFullScreenGallery
      v-if="isFullScreenActive"
      data-cy="full-screen-media"
      :active-media-item-index="activeMediaItemIndex"
      :media="mediaItems"
      :image-alt-tag="imageAltTag"
      @close="isFullScreenActive = false"
      @active-image-change="(index) => (activeMediaItemIndex = index)"
    />
  </ClientOnly>
</template>
